<template>
  <Toast />
  <ConfirmDialog></ConfirmDialog>
  <ul class="list-none p-0 m-0 flex align-items-center font-medium">
    <li>
      <a class="text-500 no-underline line-height-3 cursor-pointer">Inventario</a>
    </li>
    <li class="px-2">
      <i class="pi pi-angle-right text-500 line-height-3"></i>
    </li>
    <li>
      <span class="text-900 line-height-3">Alertas de Stock</span>
    </li>
  </ul>
  <!-- Ítem 12 (alertas_stock_resultado.md). data-testid: pruebas automáticas (e2e/) -->
  <div class="surface-section px-4 py-5 md:px-12 lg:px-12">
    <div class="flex align-items-start flex-column lg:justify-content-between lg:flex-row">
      <div>
        <div class="font-medium text-3xl text-900">
          Alertas de Stock Negativo
          <Badge size="xlarge" :severity="estado === 'pendiente' && alertas.length ? 'warning' : 'success'">
            {{ alertas.length }}
          </Badge>
        </div>
        <div class="text-600 mt-2">
          Ventas sincronizadas sin conexión que dejaron el stock en negativo. Se resuelven solas cuando el saldo vuelve
          a ser positivo (cada 15 min), o a mano.
        </div>
      </div>
      <div class="flex mt-3 lg:mt-0">
        <Button
          icon="pi pi-refresh"
          class="p-button-outlined p-button-lg"
          v-tooltip.top="'Recargar'"
          data-testid="alertas-recargar"
          :loading="cargando"
          @click="cargarAlertas"
        />
      </div>
    </div>

    <div class="p-fluid formgrid grid p-fondo mt-3">
      <div class="field col-12 md:col-4 mb-0">
        <span><strong>SUCURSAL: </strong></span>
        <Dropdown
          v-model="sucursalId"
          :options="sucursales"
          optionLabel="name"
          optionValue="id"
          placeholder="Seleccione la sucursal"
          data-testid="alertas-sucursal"
          @change="cargarAlertas"
        />
      </div>
      <div class="field col-12 md:col-4 mb-0">
        <span><strong>ESTADO: </strong></span>
        <SelectButton
          v-model="estado"
          :options="estados"
          optionLabel="label"
          optionValue="value"
          :allowEmpty="false"
          data-testid="alertas-estado"
          @change="cargarAlertas"
        />
      </div>
      <div class="field col-12 md:col-4 mb-0">
        <span><strong>BUSCAR: </strong></span>
        <InputText
          v-model="buscar['global'].value"
          placeholder="Producto o número de venta"
          data-testid="alertas-buscar"
        />
      </div>
    </div>

    <Message v-if="error" severity="error" :closable="false" class="mt-3" data-testid="alertas-error">{{ error }}</Message>

    <div class="mt-4" data-testid="alertas-tabla">
      <DataTable
        dataKey="id"
        :value="filas"
        :paginator="true"
        :rows="20"
        :loading="cargando"
        :filters="buscar"
        :globalFilterFields="['producto_texto', 'venta_texto']"
        :rowHover="true"
        class="p-datatable-sm"
        stripedRows
        showGridlines
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        :rowsPerPageOptions="[20, 100, 200]"
        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} alertas"
        responsiveLayout="scroll"
      >
        <template #empty>
          <span class="flex align-items-center justify-content-center" data-testid="alertas-vacio">
            {{
              estado === "pendiente"
                ? "No hay alertas de stock pendientes en esta sucursal."
                : "No hay alertas resueltas en esta sucursal."
            }}
          </span>
        </template>
        <Column field="created_at" header="FECHA" style="width: 130px">
          <template #body="{ data }">
            <span :data-testid="'alerta-fila-' + data.id">{{ formatoFecha(data.created_at) }}</span>
          </template>
        </Column>
        <Column field="producto_texto" header="PRODUCTO / INSUMO">
          <template #body="{ data }">
            {{ data.producto ? data.producto.descripcion : "Producto #" + data.producto_id }}
            <small v-if="data.producto && data.producto.codigo_interno" class="block text-500">
              {{ data.producto.codigo_interno }}
            </small>
          </template>
        </Column>
        <Column field="cantidad_negativa" header="FALTANTE" class="text-right" style="width: 130px">
          <template #body="{ data }">
            <span
              class="font-bold text-red-600"
              v-tooltip.top="'En insumos de receta, porciones; en productos, unidades'"
            >
              {{ formatoNumero(data.cantidad_negativa) }}
              {{ data.producto && data.producto.unidad_medida_nombre ? data.producto.unidad_medida_nombre : "" }}
            </span>
          </template>
        </Column>
        <Column field="venta_texto" header="VENTA" style="width: 110px">
          <template #body="{ data }">
            <template v-if="data.venta_id">
              <router-link
                v-if="puedeVerVenta"
                :to="'/ventas/' + data.venta_id + '/detalle'"
                :data-testid="'alerta-venta-' + data.id"
                >#{{ data.venta_id }}</router-link
              >
              <span v-else :data-testid="'alerta-venta-' + data.id">#{{ data.venta_id }}</span>
            </template>
            <span v-else>—</span>
          </template>
        </Column>
        <Column header="CAJERO / ORIGEN">
          <template #body="{ data }">
            <template v-if="data.venta">
              {{ data.venta.nombre_usuario_caja || "—" }}
              <small v-if="data.venta.origen_venta" class="block text-500">{{ data.venta.origen_venta }}</small>
            </template>
            <span v-else>—</span>
          </template>
        </Column>
        <Column field="estado" header="ESTADO" style="width: 110px">
          <template #body="{ data }">
            <span
              :class="'product-badge ' + (data.estado === 'pendiente' ? 'status-lowstock' : 'status-instock')"
              :data-testid="'alerta-estado-' + data.id"
            >
              {{ data.estado === "pendiente" ? "PENDIENTE" : "RESUELTA" }}
            </span>
          </template>
        </Column>
        <Column v-if="estado === 'resuelta'" header="RESUELTA">
          <template #body="{ data }">
            {{ formatoFecha(data.fecha_resolucion) }}
            <small class="block text-500" :data-testid="'alerta-resuelta-por-' + data.id">
              {{ data.resuelto_por ? "Manual (usuario #" + data.resuelto_por + ")" : "Automática" }}
            </small>
          </template>
        </Column>
        <Column v-if="estado === 'pendiente'" header="ACCIONES" style="width: 120px">
          <template #body="{ data }">
            <Button
              label="Resolver"
              icon="pi pi-check"
              class="p-button-sm p-button-success p-button-outlined"
              v-bloqueo-offline
              :data-testid="'alerta-resolver-' + data.id"
              :loading="resolviendo === data.id"
              @click="confirmarResolver(data)"
            />
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script>
import { FilterMatchMode } from "primevue/api";
import SelectButton from "primevue/selectbutton";
import AlertaStockService from "@/service/AlertaStockService";
import SucursalService from "@/service/SucursalService";
import { fetchWrapper } from "@/helpers";
import { useAuth } from "@/stores";

// Zona horaria del backend (config/app.php: 'timezone' => 'America/La_Paz'), la misma en la que Kardex y Ventas
// muestran sus fechas
const ZONA_APP = "America/La_Paz";

const TEXTO_SIN_PERMISO =
  "Este usuario no tiene permiso para gestionar alertas de stock (Alertas Stock Gestionar).";

export default {
  components: { SelectButton },
  data() {
    return {
      cargando: false,
      alertas: [],
      sucursales: [],
      sucursalId: null,
      estado: "pendiente",
      estados: [
        { label: "Pendientes", value: "pendiente" },
        { label: "Resueltas", value: "resuelta" },
      ],
      buscar: { global: { value: null, matchMode: FilterMatchMode.CONTAINS } },
      error: null,
      resolviendo: null,
    };
  },
  alertaService: null,
  sucursalService: null,
  auth: null,
  created() {
    this.alertaService = new AlertaStockService();
    this.sucursalService = new SucursalService();
    this.auth = useAuth();
  },
  mounted() {
    this.iniciar();
  },
  computed: {
    puedeVerVenta() {
      return "Venta Ver Detalle" in (this.auth.user.permissions || {});
    },
    // Campos de texto para el filtro global (producto y número de venta)
    filas() {
      return this.alertas.map((a) => ({
        ...a,
        producto_texto: [a.producto && a.producto.descripcion, a.producto && a.producto.codigo_interno]
          .filter(Boolean)
          .join(" "),
        venta_texto: a.venta_id ? `#${a.venta_id} ${a.venta_id}` : "",
      }));
    },
  },
  methods: {
    // D8: la sucursal de la caja abierta del usuario; si no tiene, la primera del combo
    async iniciar() {
      try {
        const combo = await this.sucursalService.getSucursalCombo();
        this.sucursales = (combo && combo.data) || [];
      } catch (e) {
        this.error = "No se pudieron cargar las sucursales: " + e;
        return;
      }
      let deLaCaja = null;
      const r = await fetchWrapper.raw("GET", `${process.env.VUE_APP_ROOT_API}/caja/verificar`, null, { timeoutMs: 8000 });
      const caja = r.ok && r.data && Array.isArray(r.data.caja) ? r.data.caja[0] : null;
      if (caja && this.sucursales.some((s) => Number(s.id) === Number(caja.sucursal_id))) deLaCaja = Number(caja.sucursal_id);
      this.sucursalId = deLaCaja || (this.sucursales[0] ? this.sucursales[0].id : null);
      this.cargarAlertas();
    },
    async cargarAlertas() {
      if (!this.sucursalId) return; // sucursal_id es obligatorio en el backend
      this.cargando = true;
      this.error = null;
      try {
        const r = await this.alertaService.listar({ sucursal_id: this.sucursalId, estado: this.estado });
        if (r.ok && r.data && r.data.success) {
          this.alertas = r.data.data || [];
        } else {
          this.alertas = [];
          if (r.status === 403) this.error = TEXTO_SIN_PERMISO;
          else if (r.status === 0) this.error = "No se pudieron cargar las alertas: sin conexión con el servidor.";
          else this.error = "No se pudieron cargar las alertas: " + ((r.data && (r.data.message || r.data.mensaje)) || `HTTP ${r.status}`);
        }
      } finally {
        this.cargando = false;
      }
    },
    confirmarResolver(alerta) {
      const producto = alerta.producto ? alerta.producto.descripcion : `producto #${alerta.producto_id}`;
      const unidad = alerta.producto && alerta.producto.unidad_medida_nombre ? ` ${alerta.producto.unidad_medida_nombre}` : "";
      const venta = alerta.venta_id ? `, venta #${alerta.venta_id}` : "";
      this.$confirm.require({
        header: "Resolver alerta",
        message:
          `¿Marcar como resuelta la alerta de ${producto} (faltante ${this.formatoNumero(alerta.cantidad_negativa)}${unidad}${venta})? ` +
          "Hágalo solo si el inventario ya se corrigió (compra, producción, traspaso o ajuste). " +
          "Si el saldo vuelve a ser positivo, el sistema la resuelve solo en unos minutos.",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Sí, resolver",
        rejectLabel: "Cancelar",
        acceptClass: "alerta-resolver-confirmar",
        rejectClass: "alerta-resolver-cancelar",
        accept: () => this.resolver(alerta),
      });
    },
    async resolver(alerta) {
      this.resolviendo = alerta.id;
      try {
        const r = await this.alertaService.resolver(alerta.id);
        if (r.ok && r.data && r.data.success) {
          this.toast("success", "Alerta resuelta.");
          this.cargarAlertas();
        } else if (r.status === 409) {
          this.toast("info", "La alerta ya estaba resuelta (por otro usuario o automáticamente).");
          this.cargarAlertas();
        } else if (r.status === 404) {
          this.toast("error", "La alerta ya no existe.");
          this.cargarAlertas();
        } else if (r.status === 403) {
          this.toast("error", TEXTO_SIN_PERMISO);
        } else {
          this.toast("error", "Sin conexión con el servidor: no se pudo resolver la alerta.");
        }
      } finally {
        this.resolviendo = null;
      }
    },
    toast(severity, detail) {
      const summary = { success: "Éxito", info: "Aviso", error: "Error" }[severity];
      this.$toast.add({ severity, summary, detail, life: severity === "success" ? 3000 : 6000 });
    },
    // CORREGIDO (ítem 12, ajuste de fechas). La versión anterior partía de un dato mal leído: el «15:24:19» del
    // volcado está en UTC (mysqldump guarda los TIMESTAMP en UTC); en la base, created_at es 11:24:19 hora de La Paz.
    // Mostrar el número tal cual daba 4 h de más. Antes:
    // formatoFecha(valor) {
    //   if (!valor) return "—";
    //   const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    //   return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : String(valor);
    // },
    // Ahora, igual que Kardex («fecha») y la lista de ventas («fecha_venta»), que muestran el texto local del servidor
    // «AAAA-MM-DD HH:mm:ss». created_at (timestamp) y fecha_resolucion (cast datetime) llegan serializados por
    // Laravel en UTC con «Z» («2026-10-12T15:24:19.000000Z»): se pasan a la zona de la app (config app.timezone =
    // America/La_Paz). Un texto sin zona («2026-10-12 11:24:19») ya es hora local y se muestra tal cual.
    formatoFecha(valor) {
      if (!valor) return "—";
      const texto = String(valor);
      if (!/(Z|[+-]\d{2}:?\d{2})$/.test(texto)) return texto.replace("T", " ").slice(0, 19);
      const fecha = new Date(texto);
      if (Number.isNaN(fecha.getTime())) return texto;
      const partes = Object.fromEntries(
        new Intl.DateTimeFormat("en-CA", {
          timeZone: ZONA_APP,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hourCycle: "h23",
        })
          .formatToParts(fecha)
          .map((p) => [p.type, p.value])
      );
      return `${partes.year}-${partes.month}-${partes.day} ${partes.hour}:${partes.minute}:${partes.second}`;
    },
    formatoNumero(valor) {
      return new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2 }).format(Number(valor) || 0);
    },
  },
};
</script>
<style scoped lang="scss">
@import "@/assets/demo/badges.scss";
.p-fondo {
  background-color: #d8fadb;
  border-radius: 5px;
  padding: 5px;
  border: 1px solid #000000;
}
</style>
